import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

// CSS
import "./rolePicker.css";

// Components
import SearchBar from "../Other Components/searchBar";
import ItemCard from "../Item Components/itemCard";
import PageLoader from "../Other Components/pageLoader";
import Title from "../Other Components/Title";
import Button from "../Other Components/button";

// Functions and hooks
import { GetRoles } from "../../BackendAccess/RoleHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useTranslation } from "react-i18next";

export default function RolePicker({
  title = "Roles",
  onChange = (selectedRoles) => {},
  initSelectedRoles,
  singleSelect = false,
  disabled,
  className,
}) {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);

  const finalClassName = twMerge("role-picker w-100 h-100", className);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const containerRef = useRef(null);

  const { showNotification } = useNotification();

  const { t } = useTranslation();

  useErrorHandler(error, showNotification, setPageError);

  const cardSecondActionHandler = (id) => {
    navigate("/workspace/roles/roleEditPage", { state: { id } });
  };

  useEffect(() => {
    if (roles.length === 0) GetAllRoles();

    if (
      selectedRoles.length === 0 &&
      initSelectedRoles &&
      initSelectedRoles.length > 0 &&
      initSelectedRoles[0] !== null &&
      initSelectedRoles[0] !== undefined
    )
      setSelectedRoles(initSelectedRoles);
  }, [initSelectedRoles]);

  useEffect(() => {
    onChange(selectedRoles);
  }, [selectedRoles]);

  const GetAllRoles = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetRoles();

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setRoles(data);
      }
    } catch (error) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (term) => {
    if (!disabled) setSearchTerm(term);
  };

  const handleSearchFocus = () => {
    if (!disabled) setSearching(true);
  };

  const handleSearchBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget) && !disabled) {
      setSearching(false);
    }
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !disabled
    ) {
      setSearching(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const filteredRoles = roles.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      item.Name.toLowerCase().includes(lowerCaseTerm) ||
      item.RoleID.toString().toLowerCase().includes(lowerCaseTerm)
    );
  });

  const sortedRoles = [...filteredRoles].sort((a, b) =>
    a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1
  );

  const onRoleSelectChanged = (id, state) => {
    if (!disabled) {
      var newSelectedIds = [...selectedRoles];
      if (state) {
        if (!singleSelect || newSelectedIds.length === 0) {
          newSelectedIds.push(id);
        } else if (newSelectedIds.length !== 0 && singleSelect) {
          newSelectedIds = [];
          newSelectedIds.push(id);
        }
      } else {
        newSelectedIds = newSelectedIds.filter((item) => item !== id);
      }
      setSelectedRoles(newSelectedIds);
    }
  };

  const createCardProps = (item) => {
    return [
      { Name: "ID", Value: item.RoleID, columnWidth: 1 },
      {
        Name: "Name",
        Value: item.Name,
        columnWidth: 3,
        Color: item.Color,
      },
    ];
  };
  const handleCreateClick = async () => {
    navigate("/workspace/roles/roleCreationPage");
  };

  return (
    <div className={finalClassName} ref={containerRef} tabIndex={-1}>
      {disabled ? <div className="disabledDimmingCover"></div> : null}
      <Title className="h-auto w-100 font-l mb-1">{title}</Title>
      <div className="role-picker-content">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          shadow={false}
          placeholder={t("Search roles...", { ns: "roles" })}
        />

        <div className="role-picker-lists-container">
          <PageLoader loading={loading} error={pageError}>
            <ul
              className={
                searching ? "role-picker-list" : "role-picker-list collapsed"
              }
            >
              {sortedRoles.map((item) => (
                <li
                  key={item.RoleID}
                  className="role-picker-list-item"
                  onClick={handleSearchFocus}
                >
                  <ItemCard
                    key={item.RoleID}
                    ItemID={item.RoleID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={false}
                    bBodyClickable={true}
                    bBtnSectionVisible={false}
                    bSelectable={true}
                    onSelected={() => {}}
                    selectedItemIds={selectedRoles}
                    setIDSelected={onRoleSelectChanged}
                    onSecondAction={cardSecondActionHandler}
                  />
                </li>
              ))}
            </ul>

            <ul className="picked-roles-list">
              {!loading && selectedRoles && selectedRoles.length > 0 ? (
                selectedRoles.map((RoleID) => {
                  const role = roles.find((cl) => cl.RoleID === RoleID);
                  return role ? (
                    <li key={RoleID} className="picked-roles-list-item">
                      <ItemCard
                        key={role.RoleID}
                        ItemID={role.RoleID}
                        propertiesToShow={createCardProps(role)}
                        bBtnVisible={false}
                        bBodyClickable={true}
                        bBtnSectionVisible={false}
                        bSelectable={true}
                        onSelected={() => {}}
                        selectedItemIds={selectedRoles}
                        setIDSelected={onRoleSelectChanged}
                      />
                    </li>
                  ) : null;
                })
              ) : (
                <p className="cardPlaceholder Role">No selected roles</p>
              )}
            </ul>
            <Button
              btnSize={"btn-normal"}
              btnStyle={"btn-positive"}
              className=" w-90 mb-2"
              btnMargin={"btn-default"}
              onClick={handleCreateClick}
              bNoShadow={true}
              icon={"create"}
            >
              {t("Create Role")}
            </Button>
          </PageLoader>
        </div>
      </div>
    </div>
  );
}
